import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState, useEffect } from "react";
import { Box, Button, Card, CheckboxField, TextField } from "@curves/ui-base";
import { Grid } from "@mui/material";
import { v4 as uuid } from "uuid";
import { refIsRequiredError, getErrorModuleStatus, callRef } from "@util/utils";
import { getCheckboxValue as getCheckboxValueTool } from "@icoach/documents/tools";
import { AddressField } from "@icoach/documents/components";
import { useReceiverClubData, useMemberDefaultReceiver } from "@icoach/healthyCyclePurchase/hooks";
import { PickItem } from "@icoach/healthyCyclePurchase/components/index";

// 比對外島關鍵字；後綴為路名（路/街/巷/弄/段/大道）時視為本島街名，不擋（例：板橋金門街）
const OFFSHORE_ADDRESS_REGEX = /(?:澎湖|金門|連江|馬祖|綠島|蘭嶼|琉球)(?!路|街|巷|弄|段|大道)/;
const EXCLUDED_RECEIVER_COUNTIES = ["澎湖縣", "金門縣", "連江縣"];
const EXCLUDED_RECEIVER_DISTRICTS = ["綠島鄉", "蘭嶼鄉", "琉球鄉"];
const normalizeCyclePurchaseAddress = address =>
	(address || "")
		.replace(/[FfＦｆ]/g, "樓")
		.replace(/[-－]/g, "之")
		.replace(/之{2,}/g, "之");  // 去除連續重複的「之」

const ReceiverSection = forwardRef(({ initialSource = {}, isSelected, onEffectTypeChange, pickItemRef, errorModule, memberID } = {}, ref) => {
	// 判斷是否為變更頁面模式（有選擇功能）
	const isChangeMode = onEffectTypeChange !== undefined;
	// 如果沒有傳入 isSelected，預設為 true（建立頁面模式）
	const shouldShow = isSelected !== undefined ? isSelected : true;

	// 使用 Hook 管理收件人常用資料
	const { formData, applyDefaultReceiver } = useMemberDefaultReceiver(memberID, initialSource);

	// 管理收件人店舖資料 Hook
	const receiverClubData = useReceiverClubData({
		isSelected: shouldShow,
	});

	const receiverNameRef = useRef(null);
	const receiverAddressRef = useRef(null);
	const receiverMobileRef = useRef(null);
	const receiverNotesRef = useRef(null);
	const receiverUseClubAddressRef = useRef(null);

	// 從 formData.source 取得初始值
	const { receiverName, receiverPostCode, receiverAddress, receiverMobile, receiverNotes } = formData.source || {};

	// 用於控制 CheckboxField 的 key，當套用會員資料時重置 checkbox
	const [checkboxKey, setCheckboxKey] = useState(uuid());
	// 用於控制 AddressField 的 key，當地址變更時重新渲染
	const [addressKey, setAddressKey] = useState(uuid());
	// 用於追蹤當前顯示的地址（用於"寄至所屬店舖"功能）
	const [currentAddress, setCurrentAddress] = useState({ postCode: receiverPostCode, address: receiverAddress });
	const [offshoreAddressError, setOffshoreAddressError] = useState({
		isError: false,
		helperText: "",
	});

	// 當 formData 變化時，更新當前地址
	useEffect(
		() => {
			if (formData.source) {
				setCurrentAddress({
					postCode: formData.source.receiverPostCode || "",
					address: formData.source.receiverAddress || "",
				});
				setAddressKey(uuid());
			}
		},
		// eslint-disable-next-line
		[formData.key],
	);

	const handleReceiverUseClubData = useCallback(async () => {
		// 呼叫 applyDefaultReceiver 取得會員收件人資料
		await applyDefaultReceiver();

		// 重置 CheckboxField（通過更新 key 強制重新渲染）
		setCheckboxKey(uuid());
	}, [applyDefaultReceiver]);

	const handleReceiverChangeClub = useCallback(
		(e, value) => {
			if (value) {
				// 當勾選"寄至所屬店舖"時，使用 getAuthClubApi 的資料
				if (receiverClubData.cyclePurchaseAddress) {
					setCurrentAddress({
						postCode: receiverClubData.cyclePurchasePostCode || "",
						address: receiverClubData.cyclePurchaseAddress || "",
					});
					setAddressKey(uuid());
				}
			}
		},
		[receiverClubData],
	);

	const getResult = useCallback(() => {
		let result = {};

		if (receiverNameRef.current && receiverNameRef.current.getResult) {
			result.receiverName = receiverNameRef.current.getResult();
		}
		if (receiverAddressRef.current && receiverAddressRef.current.getResult) {
			const { postCode, address } = receiverAddressRef.current.getResult();
			result.receiverPostCode = postCode;
			result.receiverAddress = address;
		}
		if (receiverMobileRef.current && receiverMobileRef.current.getResult) {
			result.receiverMobile = receiverMobileRef.current.getResult();
		}
		if (receiverNotesRef.current && receiverNotesRef.current.getResult) {
			result.receiverNotes = receiverNotesRef.current.getResult();
		}
		if (receiverUseClubAddressRef.current && receiverUseClubAddressRef.current.getResult) {
			result.isSendToStore = getCheckboxValueTool(receiverUseClubAddressRef.current.getResult(), {
				isMultiple: false,
			});
		}

		return result;
	}, []);

	// 當地址重置時（addressKey 更新），清除殘留的離島錯誤
	useEffect(() => {
		setOffshoreAddressError({ isError: false, helperText: "" });
		callRef(receiverAddressRef, "setErrorState", false, "");
	}, [addressKey]);

	const handlePostCodeChange = useCallback(() => {
		setOffshoreAddressError({ isError: false, helperText: "" });
		callRef(receiverAddressRef, "setErrorState", false, "");
	}, []);

	const validateOffshoreAddress = useCallback(() => {
		const { address = "" } = receiverAddressRef.current?.getResult?.() || {};
		const isOffshoreByAddress = OFFSHORE_ADDRESS_REGEX.test(address);
		const isOffshoreByPostCode = callRef(receiverAddressRef, "hasExcludedPostCode") || false;

		if (isOffshoreByAddress || isOffshoreByPostCode) {
			setOffshoreAddressError({ isError: true, helperText: "配送地址不支援離島地區" });
			callRef(receiverAddressRef, "setErrorState", true, "配送地址不支援離島地區");
			return true;
		}

		setOffshoreAddressError({ isError: false, helperText: "" });
		callRef(receiverAddressRef, "setErrorState", false, "");
		return false;
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => {
				const requiredError = refIsRequiredError(receiverNameRef, receiverAddressRef, receiverMobileRef, receiverNotesRef);
				const offshoreError = validateOffshoreAddress();
				return requiredError || offshoreError;
			},
		}),
		[getResult, validateOffshoreAddress],
	);

	return (
		<>
			<Card.CardHeader
				title={isChangeMode ? <PickItem title="收件資訊" ref={pickItemRef} checked={isSelected} handleChange={onEffectTypeChange} /> : "收件資訊"}
				className="bg-light-active px-4"
			/>
			{shouldShow && (
				<Card.CardContent key={formData.key} className="p-4">
					<Grid spacing={2} container>
						<Grid xs={12} item>
							<Box className="tool-btn-group">
								<Button color="gray" onClick={handleReceiverUseClubData}>
									同會員資料
								</Button>
							</Box>
						</Grid>
						<Grid xs={4} item>
							<TextField label="收件人" ref={receiverNameRef} defaultValue={receiverName} {...getErrorModuleStatus(errorModule, "receiverName")} fullWidth required />
						</Grid>
						<Grid xs={4} item>
							<TextField
								label="收件手機"
								ref={receiverMobileRef}
								defaultValue={receiverMobile}
								{...getErrorModuleStatus(errorModule, "receiverMobile")}
								maskType="MOB"
								fullWidth
								required
							/>
						</Grid>
						<Grid xs={12} item>
							{(() => {
								const addressErrorKey = errorModule?.receiverAddress ? "receiverAddress" : "receiverPostCode";
								const addressErrorStatus = getErrorModuleStatus(errorModule, addressErrorKey);
								return (
									<>
										<CheckboxField key={checkboxKey} ref={receiverUseClubAddressRef} onChange={handleReceiverChangeClub}>
											<CheckboxField.Checkbox label={<span className="font-weight-bold">寄至所屬店舖</span>} value={true} />
										</CheckboxField>
										<AddressField
											key={addressKey}
											label="收件地址"
											ref={receiverAddressRef}
											postCode={currentAddress.postCode}
											address={currentAddress.address}
											excludedCounties={EXCLUDED_RECEIVER_COUNTIES}
											excludedDistricts={EXCLUDED_RECEIVER_DISTRICTS}
											addressNormalizer={normalizeCyclePurchaseAddress}
											addressPlaceholder="請勿填寫便利商店地址、郵政信箱"
											{...addressErrorStatus}
											isError={Boolean(addressErrorStatus?.isError) || offshoreAddressError.isError}
											helperText={offshoreAddressError.isError ? offshoreAddressError.helperText : addressErrorStatus?.helperText}
											containerSpacing={3}
											onPostCodeChange={handlePostCodeChange}
											required
										/>
									</>
								);
							})()}
						</Grid>
						<Grid xs={12} item>
							<TextField label="備註" ref={receiverNotesRef} defaultValue={receiverNotes} {...getErrorModuleStatus(errorModule, "receiverNotes")} minRows={3} multiline fullWidth />
						</Grid>
					</Grid>
				</Card.CardContent>
			)}
		</>
	);
});

ReceiverSection.displayName = "ReceiverSection";

export default ReceiverSection;
